import type { OrderDetails } from '../../model';
import { formatDateRu, getOrderStatusConfig } from '../../utils';

type Props = {
  order: OrderDetails;
  onClick?: () => void;
};

const PillIcon = () => (
  <svg
    className="size-[24px] shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.5">
      <path
        d="M12.4644 4.03551C14.417 2.08289 17.5828 2.08289 19.5355 4.03551C21.4881 5.98813 21.4881 9.15396 19.5355 11.1066L11.0502 19.5919C9.09756 21.5445 5.93174 21.5445 3.97911 19.5919C2.02649 17.6392 2.02649 14.4734 3.97911 12.5208L12.4644 4.03551Z"
        fill="#CBE2EF"
      />
      <path
        d="M8.22168 8.27808L15.2927 15.3491"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.8184 7.92454L14.9397 5.80322"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

const getCardWrapperBg = () => 'bg-[#e4f0f7]';

export const ClientCurrentOrderCard = ({ order, onClick }: Props) => {
  const orderStatus = getOrderStatusConfig(order.status?.value ?? '');
  const isPaid =
    order.status?.code_integer === 3 || order.status?.code === 'PAID';
  const wrapperBg = getCardWrapperBg();

  const formatMedicineCount = () => {
    const count = order.medicine_count || 0;
    if (count === 1) return '1 позиция';
    if (count >= 2 && count <= 4) return `${count} позиции`;
    return `${count} позиций`;
  };

  const orderDate = order.create_date
    ? formatDateRu(order.create_date, 'd MMMM, HH:mm')
    : null;

  const formatClinicOrAddress = () => {
    const clinic =
      order.hospital?.name ??
      (order as { clinic_name?: string }).clinic_name ??
      (order.delivery_address as { name?: string } | undefined)?.name ??
      (order.delivery_address as { clinic_name?: string } | undefined)
        ?.clinic_name;
    const address = order.delivery_address?.address_text;
    return clinic || address || '—';
  };

  return (
    <div
      className={`flex w-full cursor-pointer flex-col overflow-hidden rounded-[16px] p-[6px] ${wrapperBg}`}
      onClick={onClick}
    >
      <div className="flex flex-col">
        <div className="flex items-start justify-start gap-[8px] self-stretch rounded-t-[16px] bg-transparent p-[10px]">
          <div className="flex flex-1 flex-wrap items-center gap-x-[12px] gap-y-[4px]">
            <div className="flex items-start gap-[12px]">
              <div className="text-[16px] font-normal leading-5 text-[#171717]">
                №{order.id}
              </div>
            </div>
            <div
              className={`flex h-[28px] items-center justify-center gap-[10px] rounded-[100px] px-[12px] py-[4px] ${orderStatus.bg}`}
            >
              <span
                className={`text-[12px] font-medium leading-4 ${orderStatus.text}`}
              >
                {orderStatus.label}
              </span>
            </div>
            {orderDate && (
              <span className="whitespace-nowrap text-[14px] font-normal leading-5 text-[#171717] opacity-40">
                {orderDate}
              </span>
            )}
          </div>
          {!isPaid && order.payment_link && (
            <a
              href={order.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex shrink-0 items-center justify-center gap-[10px] rounded-[8px] bg-[#0ea5e9] px-[12px] py-[6px] text-[14px] font-medium leading-5 text-white hover:bg-[#0284c7]"
            >
              Оплатить
            </a>
          )}
        </div>

        <div className="flex flex-col self-stretch rounded-b-[12px] rounded-t-[16px] bg-white p-[16px]">
          <div className="flex flex-col gap-[12px] self-stretch py-[8px] md:flex-row md:flex-wrap md:justify-between">
            <div className="inline-flex items-center gap-[8px] self-stretch">
              <PillIcon />
              <div className="min-w-0 flex-1 text-[14px] font-normal leading-5 text-[#171717]">
                {formatMedicineCount()}
              </div>
            </div>
            <div className="w-full self-stretch">
              <div className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[14px] font-normal leading-5 text-[#171717]">
                {order.last_comment && order.last_comment.trim()
                  ? order.last_comment.trim()
                  : '—'}
              </div>
            </div>
            <div className="inline-flex items-center gap-[8px] self-stretch">
              <div className="min-w-0 flex-1 truncate text-[14px] font-normal leading-5 text-[#171717]">
                {formatClinicOrAddress()}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0 self-stretch border-t border-[#f3f4f6] pt-[16px]">
            <span className="text-[14px] font-normal leading-5 text-[#171717] opacity-40">
              Сформировал(а) заказ:
            </span>
            <span className="text-[14px] font-normal leading-5 text-[#171717] underline">
              {order.doctor?.fullName || 'Вы'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
